/**
 * PDP reviews: one data layer over Klaviyo Reviews and Fera (public, read-only
 * client APIs), used by the "Customer Reviews" section (<pdp-reviews>) and the
 * price-row rating (<review-stars>) so both show the same numbers.
 *
 * Sources and keys come from Theme settings > Third-party apps, rendered onto
 * each element as data attributes: data-sources="klaviyo,fera",
 * data-klaviyo-company, data-fera-key, data-product-id.
 */

const KLAVIYO_API = 'https://fast.a.klaviyo.com/reviews/api/client_reviews';
const KLAVIYO_MEDIA = 'https://klaviyo.s3.amazonaws.com/reviews/images/';
const KLAVIYO_THUMB = 'https://reviews-media.services.klaviyo.com/abc/width:300/height:300/resizing_type:fill/plain/';
const FERA_API = 'https://cdn.fera.ai/api/v3/public';
/** Writes (helpful votes) go to the uncached API host, as Fera's own widget does. */
const FERA_WRITE_API = 'https://api.fera.ai/v3/public';
const HELPFUL_STORAGE_KEY = 'pdp-reviews:helpful';

/** Sort keys shared by both sources, with each source's own parameter. */
const SORTS = {
  recent: { klaviyo: 0, fera: 'created_at:desc' },
  rating_desc: { klaviyo: 1, fera: 'rating:desc' },
  rating_asc: { klaviyo: 2, fera: 'rating:asc' },
};

const COMPARE = {
  recent: (a, b) => b.date - a.date,
  rating_desc: (a, b) => b.rating - a.rating || b.date - a.date,
  rating_asc: (a, b) => a.rating - b.rating || b.date - a.date,
};

async function getJson(url) {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${response.status} ${url}`);
  return response.json();
}

function withParams(base, params) {
  const url = new URL(base);
  for (const [key, value] of Object.entries(params)) {
    if (value !== '' && value != null) url.searchParams.set(key, String(value));
  }
  return url;
}

/** @typedef {{ average: number, count: number, histogram: number[] }} Rating  histogram[0] = 1 star … [4] = 5 stars */
/** @typedef {{ thumb: string, url: string, alt: string }} Media */
/** @typedef {{ id: string, rating: number, title: string, body: string, author: string, location: string, date: Date, media: Media[], helpful: number | null }} Review */

class KlaviyoSource {
  constructor(companyId, productId) {
    this.companyId = companyId;
    this.productId = productId;
    this.base = `${KLAVIYO_API}/${productId}/`;
  }

  #fetch(params) {
    return getJson(
      withParams(this.base, {
        product_id: this.productId,
        company_id: this.companyId,
        type: 'reviews',
        media: false,
        filter: '',
        ...params,
      })
    );
  }

  #summary() {
    this.summaryPromise ??= this.#fetch({ limit: 1, offset: 0, sort: 0 });
    return this.summaryPromise;
  }

  /** @returns {Promise<Rating>} */
  async rating() {
    const { product = {} } = await this.#summary();
    return {
      average: product.star_rating || 0,
      count: product.review_count || 0,
      histogram: product.rating_histogram || [0, 0, 0, 0, 0],
    };
  }

  /** @returns {Promise<Media[]>} */
  async media() {
    const { summary = {} } = await this.#summary();
    return (summary.review_images || []).map((image) => ({
      thumb: KLAVIYO_THUMB + KLAVIYO_MEDIA + image.uuid,
      url: KLAVIYO_MEDIA + image.uuid,
      alt: image.title || '',
    }));
  }

  /** @returns {Promise<{ reviews: Review[], total: number }>} */
  async reviews({ limit, sort, search }) {
    const data = await this.#fetch({ limit, offset: 0, sort: SORTS[sort].klaviyo, filter: search });
    return {
      total: data.filtered_count ?? 0,
      reviews: (data.reviews || []).map((review) => ({
        id: `k${review.id}`,
        rating: review.rating || 0,
        title: review.title || '',
        body: review.content || '',
        author: review.author || '',
        location: '',
        date: new Date(review.created_at),
        media: review.image_uuid
          ? [{ thumb: KLAVIYO_THUMB + KLAVIYO_MEDIA + review.image_uuid, url: KLAVIYO_MEDIA + review.image_uuid, alt: review.content || '' }]
          : [],
        helpful: null,
      })),
    };
  }
}

class FeraSource {
  constructor(apiKey, productId) {
    this.apiKey = apiKey;
    this.productId = productId;
  }

  #fetch(path, params = {}) {
    return getJson(withParams(`${FERA_API}/${path}`, { api_key: this.apiKey, ...params }));
  }

  /** @returns {Promise<Rating>} */
  async rating() {
    // The reviews endpoint's aggregate has per-star counts; rating.json can return `counts: null`.
    const data = await this.#fetch(`products/${this.productId}/reviews.json`, { page_size: 1, include_aggregate_rating: true });
    const rating = data.product?.rating || data.rating || {};
    const count = rating.count || 0;
    const average = rating.average || 0;
    const counts = rating.counts || [0, 0, 0, 0, 0];

    // Fera doesn't document the `counts` order; keep whichever order matches the average.
    const averageOf = (list) => list.reduce((sum, n, i) => sum + n * (i + 1), 0) / (count || 1);
    const ascending = [...counts];
    const descending = [...counts].reverse();
    const histogram =
      Math.abs(averageOf(ascending) - average) <= Math.abs(averageOf(descending) - average) ? ascending : descending;
    return { average, count, histogram };
  }

  /** @returns {Promise<Media[]>} */
  async media() {
    const { data = [] } = await this.#fetch(`products/${this.productId}/media.json`, { page_size: 30 });
    return data
      .filter((item) => item.thumbnail_url || item.url)
      .map((item) => ({ thumb: item.thumbnail_url || item.url, url: item.url, alt: item.alt_text || item.caption || '' }));
  }

  /** @returns {Promise<{ reviews: Review[], total: number }>} */
  async reviews({ limit, sort, search }) {
    const { data = [], meta = {} } = await this.#fetch(`products/${this.productId}/reviews.json`, {
      page: 1,
      page_size: limit,
      sort_by: SORTS[sort].fera,
      search,
    });
    return {
      total: meta.total_count ?? data.length,
      reviews: data.map((review) => ({
        id: review.id,
        rating: review.rating || 0,
        title: review.heading || '',
        body: review.body || '',
        author: review.customer?.display_name || '',
        location: review.customer?.display_location || '',
        date: new Date(review.created_at),
        media: (review.media || []).map((item) => ({
          thumb: item.thumbnail_url || item.url,
          url: item.url,
          alt: item.alt_text || '',
        })),
        helpful: review.counts?.helpful ?? 0,
        source: this,
      })),
    };
  }

  /** Marks (or unmarks) a review as helpful; resolves to the new helpful count. */
  async setHelpful(reviewId, helpful) {
    const url = withParams(`${FERA_WRITE_API}/reviews/${encodeURIComponent(reviewId)}/likes`, { api_key: this.apiKey });
    const response = await fetch(url, {
      method: helpful ? 'POST' : 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: helpful ? '{}' : undefined,
    });
    const data = await response.json().catch(() => ({}));
    // Fera answers "already_marked_helpful" / "not_marked_helpful" with the review in `data`.
    const review = data.data || data;
    return review.counts?.helpful;
  }
}

function readHelpfulVotes() {
  try {
    return new Set(JSON.parse(localStorage.getItem(HELPFUL_STORAGE_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

function writeHelpfulVotes(votes) {
  try {
    localStorage.setItem(HELPFUL_STORAGE_KEY, JSON.stringify([...votes]));
  } catch {
    // Storage unavailable: the vote still counts, it just won't be remembered.
  }
}

/** All configured sources for a product, shared by every element on the page. */
const registry = new Map();

function sourcesFor(element) {
  const { productId, sources = '', klaviyoCompany, feraKey } = element.dataset;
  const key = [productId, sources, klaviyoCompany, feraKey].join('|');
  if (!registry.has(key)) {
    const list = [];
    for (const name of sources.split(',').map((s) => s.trim())) {
      if (name === 'klaviyo' && klaviyoCompany) list.push(new KlaviyoSource(klaviyoCompany, productId));
      if (name === 'fera' && feraKey) list.push(new FeraSource(feraKey, productId));
    }
    registry.set(key, { list, rating: null });
  }
  return registry.get(key);
}

/** @returns {Promise<Rating>} combined across sources */
function combinedRating(entry) {
  entry.rating ??= Promise.allSettled(entry.list.map((source) => source.rating())).then((results) => {
    const combined = { average: 0, count: 0, histogram: [0, 0, 0, 0, 0] };
    let total = 0;
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      const { average, count, histogram } = result.value;
      combined.count += count;
      total += average * count;
      histogram.forEach((n, i) => (combined.histogram[i] += n || 0));
    }
    combined.average = combined.count ? total / combined.count : 0;
    return combined;
  });
  return entry.rating;
}

async function combinedMedia(entry) {
  const results = await Promise.allSettled(entry.list.map((source) => source.media()));
  return results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
}

/** One page of the merged list: the first `page * perPage` from each source, merged and sliced. */
async function combinedReviews(entry, { page, perPage, sort, search }) {
  const limit = page * perPage;
  const results = await Promise.allSettled(entry.list.map((source) => source.reviews({ limit, sort, search })));
  let total = 0;
  const merged = [];
  for (const result of results) {
    if (result.status !== 'fulfilled') continue;
    total += result.value.total;
    merged.push(...result.value.reviews);
  }
  merged.sort(COMPARE[sort]);
  return { total, reviews: merged.slice((page - 1) * perPage, page * perPage) };
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text ?? '';
  return div.innerHTML;
}

/** Star row: each star is the Figma star icon, with a clipped copy on top for the filled part. */
function starsHtml(starIcon, rating, className) {
  let html = `<span class="review-stars__stars ${className}" role="img" aria-label="${Number(rating).toFixed(1)} out of 5 stars">`;
  for (let i = 0; i < 5; i++) {
    const fill = Math.max(0, Math.min(1, rating - i)) * 100;
    html += `<span class="review-stars__star">${starIcon}<span class="review-stars__star-fill" style="width:${fill}%">${starIcon}</span></span>`;
  }
  return `${html}</span>`;
}

/**
 * Price-row rating (Figma 910:8018): stars + underlined (count) linking to the reviews section.
 * data-variant="card" (product cards, snippets/review-stars-card.liquid): plain (count), no link.
 */
class ReviewStars extends HTMLElement {
  async connectedCallback() {
    const entry = sourcesFor(this);
    if (!entry.list.length) return;
    const starIcon = this.querySelector('template')?.innerHTML || '';
    const { average, count } = await combinedRating(entry);
    const target = this.dataset.target || '#customer-reviews';
    const countHtml =
      this.dataset.variant === 'card'
        ? `<span class="review-stars__count">(${count})</span>`
        : `<a class="review-stars__count" href="${target}">(${count})</a>`;
    this.querySelector('[data-stars]').innerHTML =
      `${starsHtml(starIcon, average, 'review-stars__stars--sm')}${countHtml}`;
    this.hidden = false;
  }
}

class PdpReviews extends HTMLElement {
  connectedCallback() {
    this.entry = sourcesFor(this);
    this.perPage = Number(this.dataset.perPage) || 3;
    this.page = 1;
    this.sort = 'recent';
    this.search = '';
    this.starIcon = this.querySelector('[data-star-full]')?.innerHTML || '';
    this.thumbIcon = this.querySelector('[data-thumb-icon]')?.innerHTML || '';
    this.helpfulVotes = readHelpfulVotes();
    this.pageReviews = new Map();
    if (!this.entry.list.length) return;

    this.querySelector('[data-list]')?.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-helpful]') : null;
      if (button) this.#toggleHelpful(button);
    });

    this.querySelectorAll('[data-write-review]').forEach((button) =>
      button.addEventListener('click', () => this.#openWriteReview())
    );

    const sort = this.querySelector('[data-sort]');
    sort?.addEventListener('change', () => {
      this.sort = sort.value;
      this.querySelector('[data-sort-label]').textContent = sort.selectedOptions[0].textContent;
      this.page = 1;
      this.#loadReviews();
    });

    let searchTimer;
    this.querySelector('[data-search]')?.addEventListener('input', (event) => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => {
        this.search = event.target.value.trim();
        this.page = 1;
        this.#loadReviews();
      }, 300);
    });

    this.querySelector('[data-page-prev]')?.addEventListener('click', () => this.#goToPage(this.page - 1));
    this.querySelector('[data-page-next]')?.addEventListener('click', () => this.#goToPage(this.page + 1));

    const track = this.querySelector('[data-media-track]');
    this.querySelector('[data-media-next]')?.addEventListener('click', () => {
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      track.scrollTo({ left: atEnd ? 0 : track.scrollLeft + track.clientWidth, behavior: 'smooth' });
    });

    this.#loadRating();
    this.#loadMedia();
    this.#loadReviews();
  }

  async #loadRating() {
    const { average, count, histogram } = await combinedRating(this.entry);
    for (let stars = 1; stars <= 5; stars++) {
      const n = histogram[stars - 1] || 0;
      this.querySelector(`[data-bar="${stars}"]`).style.width = count ? `${(n / count) * 100}%` : '0%';
      this.querySelector(`[data-bar-count="${stars}"]`).textContent = n;
    }

    this.querySelector('[data-average]').textContent = average.toFixed(1);
    this.querySelector('[data-average-stars]').innerHTML = starsHtml(this.starIcon, average, 'review-stars__stars--lg');
    this.querySelector('[data-count-text]').textContent = `${count} ${count === 1 ? 'review' : 'reviews'}`;

    const recommend = this.querySelector('[data-recommend]');
    if (recommend && count) {
      const positive = (histogram[3] || 0) + (histogram[4] || 0);
      recommend.textContent = `${positive} out of ${count} (${Math.round((positive / count) * 100)}%) recommend this product`;
      recommend.hidden = false;
    }
  }

  async #loadMedia() {
    const media = await combinedMedia(this.entry);
    if (!media.length) return;
    this.querySelector('[data-media-track]').innerHTML = media
      .map(
        (item) => `<a class="pdp-reviews__media-item" href="${escapeHtml(item.url)}" target="_blank" rel="noopener">
          <img src="${escapeHtml(item.thumb)}" alt="${escapeHtml(item.alt)}" loading="lazy" width="150" height="150">
        </a>`
      )
      .join('');
    this.querySelector('[data-media-block]').hidden = false;
  }

  async #loadReviews() {
    const { reviews, total } = await combinedReviews(this.entry, {
      page: this.page,
      perPage: this.perPage,
      sort: this.sort,
      search: this.search,
    });
    this.pageCount = Math.ceil(total / this.perPage);
    this.pageReviews = new Map(reviews.map((review) => [review.id, review]));
    this.querySelector('[data-list]').innerHTML = reviews.map((review) => this.#review(review)).join('');
    this.querySelector('[data-empty]').hidden = reviews.length > 0;
    this.#renderPagination();
  }

  #review(review) {
    const media = review.media
      .map(
        (item) =>
          `<a class="pdp-reviews__review-media" href="${escapeHtml(item.url)}" target="_blank" rel="noopener"><img src="${escapeHtml(item.thumb)}" alt="${escapeHtml(item.alt)}" loading="lazy" width="140" height="140"></a>`
      )
      .join('');
    return `<article class="pdp-reviews__review">
      <div class="pdp-reviews__reviewer">
        <p class="pdp-reviews__reviewer-name">${escapeHtml(review.author || 'Anonymous')}</p>
        ${review.location ? `<p class="pdp-reviews__reviewer-meta">${escapeHtml(review.location)}</p>` : ''}
      </div>
      <div class="pdp-reviews__details">
        <div class="pdp-reviews__details-text">
          <div class="pdp-reviews__title-group">
            ${starsHtml(this.starIcon, review.rating, 'review-stars__stars--md')}
            ${review.title ? `<h3 class="pdp-reviews__review-title">${escapeHtml(review.title)}</h3>` : ''}
          </div>
          <p class="pdp-reviews__date">${this.#timeAgo(review.date)}</p>
          ${review.body ? `<p class="pdp-reviews__review-body">${escapeHtml(review.body)}</p>` : ''}
        </div>
        ${media ? `<div class="pdp-reviews__review-media-list">${media}</div>` : ''}
        ${
          review.helpful != null
            ? `<p class="pdp-reviews__helpful">Helpful? <button type="button" class="pdp-reviews__helpful-button button-unstyled" data-helpful="${escapeHtml(review.id)}" aria-pressed="${this.helpfulVotes.has(review.id)}" aria-label="Mark review as helpful"><span class="pdp-reviews__thumb-up" aria-hidden="true">${this.thumbIcon}</span>(<span data-helpful-count>${review.helpful}</span>)</button></p>`
            : ''
        }
      </div>
    </article>`;
  }

  #renderPagination() {
    const nav = this.querySelector('[data-pagination]');
    nav.hidden = this.pageCount <= 1;
    if (nav.hidden) return;

    // Figma: first pages, an ellipsis, then the last page.
    const pages = new Set([1, this.pageCount, this.page]);
    for (let i = 1; i <= Math.min(4, this.pageCount); i++) pages.add(i);
    const sorted = [...pages].sort((a, b) => a - b);
    let html = '';
    sorted.forEach((page, i) => {
      if (i > 0 && page - sorted[i - 1] > 1) html += '<span class="pdp-reviews__page-gap">...</span>';
      html += `<button type="button" class="pdp-reviews__page button-unstyled" data-page="${page}"${page === this.page ? ' aria-current="page"' : ''}>${page}</button>`;
    });
    const container = this.querySelector('[data-pages]');
    container.innerHTML = html;
    container.querySelectorAll('[data-page]').forEach((button) =>
      button.addEventListener('click', () => this.#goToPage(Number(button.dataset.page)))
    );
    this.querySelector('[data-page-prev]').disabled = this.page <= 1;
    this.querySelector('[data-page-next]').disabled = this.page >= this.pageCount;
  }

  #goToPage(page) {
    if (page < 1 || page > this.pageCount || page === this.page) return;
    this.page = page;
    this.#loadReviews().then(() =>
      this.querySelector('.pdp-reviews__filters')?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    );
  }

  async #toggleHelpful(button) {
    const review = this.pageReviews.get(button.dataset.helpful);
    if (!review?.source?.setHelpful || button.disabled) return;

    const helpful = !this.helpfulVotes.has(review.id);
    const countEl = button.querySelector('[data-helpful-count]');
    button.disabled = true;
    try {
      const count = await review.source.setHelpful(review.id, helpful);
      if (helpful) this.helpfulVotes.add(review.id);
      else this.helpfulVotes.delete(review.id);
      writeHelpfulVotes(this.helpfulVotes);
      review.helpful = count ?? Math.max(0, review.helpful + (helpful ? 1 : -1));
      countEl.textContent = review.helpful;
      button.setAttribute('aria-pressed', String(helpful));
    } catch (error) {
      console.warn('[pdp-reviews] helpful', error);
    } finally {
      button.disabled = false;
    }
  }

  #openWriteReview() {
    // Press the app's own "Write a review" button: Klaviyo's product reviews block or Fera's widget.
    const widget = this.querySelector('.pdp-reviews__app-widget');
    const button =
      (this.dataset.sources.includes('klaviyo') && widget?.querySelector('.kl_reviews__button')) ||
      widget?.querySelector('.fera-reviews-writeReviewLink');
    button?.click();
  }

  #timeAgo(date) {
    if (Number.isNaN(date.getTime())) return '';
    const seconds = (date.getTime() - Date.now()) / 1000;
    const units = [
      ['year', 31536000],
      ['month', 2592000],
      ['week', 604800],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
    ];
    const rtf = new Intl.RelativeTimeFormat(document.documentElement.lang || 'en', { numeric: 'auto' });
    const [unit, size] = units.find(([, size]) => Math.abs(seconds) >= size) || ['minute', 60];
    return rtf.format(Math.round(seconds / size), unit).replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
  }
}

if (!customElements.get('review-stars')) customElements.define('review-stars', ReviewStars);
if (!customElements.get('pdp-reviews')) customElements.define('pdp-reviews', PdpReviews);
